import React, { Reducer, useEffect, useReducer } from 'react';

export interface ConnectionContext extends ConnectionState {
  setIsInFocus: (isInFocus: boolean) => void;
}

interface ConnectionState {
  isInFocus: boolean;
}

type Action = {
  type: 'SET_IS_IN_FOCUS';
  payload: { isInFocus: boolean };
};

const initialState: ConnectionState = {
  isInFocus: true,
};

const connectionReducer = (state: ConnectionState, action: Action): ConnectionState => {
  switch (action.type) {
    case 'SET_IS_IN_FOCUS':
      return {
        ...state,
        isInFocus: action.payload.isInFocus,
      };
    default:
      return state;
  }
};

const ConnectionContext = React.createContext<ConnectionContext | null>(null);

export const ConnectionProvider = ({ children }: { children: React.ReactNode }) => {
  const [state, dispatch] = useReducer<Reducer<ConnectionState, Action>>(connectionReducer, initialState);

  const setIsInFocus = (isInFocus: boolean) => {
    dispatch({ type: 'SET_IS_IN_FOCUS', payload: { isInFocus } });
  };

  useEffect(() => {
    if (typeof document === 'undefined') return;

    const updateFocus = () => {
      setIsInFocus(document.hasFocus());
    };
    updateFocus();

    window.addEventListener('focus', updateFocus);
    window.addEventListener('blur', updateFocus);
    document.addEventListener('visibilitychange', updateFocus);

    return () => {
      window.removeEventListener('focus', updateFocus);
      window.removeEventListener('blur', updateFocus);
      document.removeEventListener('visibilitychange', updateFocus);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const values = React.useMemo(
    () => ({
      ...state,
      setIsInFocus,
    }),
    [state]
  );

  return <ConnectionContext.Provider value={values}>{children}</ConnectionContext.Provider>;
};

export const useConnection = (): ConnectionContext => {
  const context = React.useContext(ConnectionContext);

  if (context === undefined) {
    throw new Error('`useConnection` hook must be used within a `ConnectionProvider` component');
  }
  return context as ConnectionContext;
};
